import React from 'react';
import {Box, Typography, Stack, Chip, Divider, Grid} from '@mui/material';
import {Form, FormSubmission} from "crqlar-react-common/dist/models/forms/form";
import {Media} from "crqlar-react-common/dist/models/common/media.model";
import {formatDateDMYHM} from "crqlar-react-common/dist/utils/date.util";
import MdRenderer from "crqlar-react-common/dist/components/MdRenderer";
import {getScaledUrl, MediaSize} from "crqlar-react-common/dist/utils/media.util";
import {useTranslation} from "react-i18next";

interface PrintableSubmissionProps {
  submission: FormSubmission;
  form?: Form;
}

const PrintableSubmission = React.forwardRef<HTMLDivElement, PrintableSubmissionProps>(
  ({submission, form}, ref) => {
    const {t} = useTranslation();

    // Helper function to check if a value is a Media object
    const isMedia = (value: any): value is Media => {
      return value && typeof value === 'object' && 'mediaURI' in value;
    };

    // Helper function to check if a value is an array of Media objects
    const isMediaArray = (value: any): value is Media[] => {
      return Array.isArray(value) && value.length > 0 && isMedia(value[0]);
    };

    // Helper function to get question title by ID from form data
    const getQuestionTitle = (questionId: string): string => {
      if (!form?.questions) return questionId;
      const question = form.questions.find(q => q.id === questionId);
      return question?.title || questionId;
    };

    // Format submission date
    const submittedDate = formatDateDMYHM(submission.insertDate)

    return (
      <Box ref={ref} className="printable-submission" sx={{p: 4}}>
        {/* Header */}
        <Box sx={{mb: 1}}>
          <Typography variant="h3" gutterBottom>
            {form?.title || t('Form Submission')}
          </Typography>
          {form?.description && (
            <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
              {form.description}
            </Typography>
          )}
          <Divider sx={{my: 2}} />
          <Stack  spacing={2}>
            <Stack spacing={1}>
              <Typography variant="caption" color="text.secondary">
                {t('Submitted On')}
              </Typography>
              <Typography variant="body1">
                {submittedDate}
              </Typography>
            </Stack>
            {submission.email && (
              <Stack spacing={1}>
                <Typography variant="caption" color="text.secondary">
                  {t('Email')}
                </Typography>
                <Typography variant="body1">
                  {submission.email}
                </Typography>
              </Stack>
            )}
            {submission.phone && (
              <Stack spacing={1}>
                <Typography variant="caption" color="text.secondary">
                  {t('Phone')}
                </Typography>
                <Typography variant="body1">
                  {submission.phone}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Box>

        <Divider sx={{my: 3}} />

        {/* Questions and Answers */}
        <Stack spacing={3}>
          {submission.answers?.map((answer, index) => {
            const questionTitle = answer.questionId
              ? getQuestionTitle(answer.questionId)
              : answer.questionTitle;

            return (
              <Box key={index} sx={{breakInside: 'avoid'}}>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{mb: 1, color: 'primary.main'}}
                >
                  {questionTitle}
                </Typography>
                {answer.questionDescription && (
                  <Box
                    sx={{
                      mb: 1,
                      color: 'text.secondary',
                      '& p': {fontSize: '0.875rem', margin: '0.5em 0'},
                      '& ul, & ol': {paddingLeft: '1.5em', margin: '0.5em 0'},
                      '& h1, & h2, & h3': {fontSize: '1rem', fontWeight: 600, margin: '0.75em 0 0.5em'},
                      '& code': {
                        backgroundColor: 'rgba(0, 0, 0, 0.05)',
                        padding: '0.2em 0.4em',
                        borderRadius: '3px',
                        fontSize: '0.85em'
                      },
                      '& pre': {
                        backgroundColor: 'rgba(0, 0, 0, 0.05)',
                        padding: '1em',
                        borderRadius: '4px',
                        overflow: 'auto'
                      }
                    }}
                  >
                    <MdRenderer>{answer.questionDescription}</MdRenderer>
                  </Box>
                )}

                {/* Handle different answer types */}
                {isMedia(answer.answer) ? (
                  <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    <Box
                      component="img"
                      src={getScaledUrl(answer.answer as Media, MediaSize.LARGE_600, MediaSize.LARGE_600)}
                      alt={(answer.answer as Media).publicId || 'Media'}
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: 1,
                        objectFit: 'cover',
                        border: '1px solid',
                        borderColor: 'divider'
                      }}
                    />
                  </Box>
                ) : isMediaArray(answer.answer) ? (
                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{gap: 1}}>
                    {(answer.answer as Media[]).map((media, idx) => (
                      <Box key={idx} sx={{breakInside: 'avoid'}}>
                        <Box
                          component="img"
                          src={getScaledUrl(answer.answer as Media, MediaSize.LARGE_600, MediaSize.LARGE_600)}
                          alt={(answer.answer as Media).publicId || 'Media'}
                          sx={{
                            width: 80,
                            height: 80,
                            borderRadius: 1,
                            objectFit: 'cover',
                            border: '1px solid',
                            borderColor: 'divider'
                          }}
                        />
                      </Box>
                    ))}
                  </Stack>
                ) : Array.isArray(answer.answer) ? (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{gap: 0.5}}>
                    {answer.answer.map((value, idx) => (
                      <Chip
                        key={idx}
                        label={String(value)}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                ) : typeof answer.answer === 'boolean' ? (
                  <Chip
                    label={answer.answer ? t('Yes') : t('No')}
                    size="small"
                    color={answer.answer ? 'success' : 'default'}
                    variant="outlined"
                  />
                ) : (
                  <Typography variant="body1" sx={{whiteSpace: 'pre-wrap'}}>
                    {String(answer.answer)}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Stack>

        {/* Footer */}
        <Box sx={{mt: 6, pt: 3, borderTop: '1px solid', borderColor: 'divider'}}>
          <Typography variant="caption" color="text.secondary">
            {t('Submission ID:')} {submission.id}
          </Typography>
        </Box>
      </Box>
    );
  }
);

PrintableSubmission.displayName = 'PrintableSubmission';

export default PrintableSubmission;
